import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Paperclip, PaperPlaneRight, X, CheckCircle, ArrowClockwise,
  Heartbeat, FloppyDisk,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { v: "operante", label: "Operante" },
  { v: "instabilidade", label: "Instabilidade" },
  { v: "manutencao", label: "Manutenção" },
];
const STATUS_META = {
  operante: { color: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]", dot: "bg-[#22C55E]" },
  instabilidade: { color: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", dot: "bg-[#F59E0B]" },
  manutencao: { color: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]", dot: "bg-[#DC2626]" },
};

export default function MasterSupport() {
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("open");
  const [openView, setOpenView] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [reply, setReply] = useState({ body: "", attachments: [] });
  const [status, setStatus] = useState({ overall_status: "operante", message: "", whatsapp_number: "" });
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingReply, setSavingReply] = useState(false);
  const replyAttachRef = useRef(null);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/support/tickets"),
        api.get("/service-status"),
      ]);
      setTickets(t.data);
      setStatus({
        overall_status: s.data.overall_status || "operante",
        message: s.data.message || "",
        whatsapp_number: s.data.whatsapp_number || "",
      });
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const readAsBase64 = (file) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve({
      filename: file.name, mimetype: file.type || "application/octet-stream",
      data: String(r.result || "").split(",")[1] || "",
    });
    r.readAsDataURL(file);
  });

  const handleAttachments = async (files) => {
    const list = Array.from(files || []);
    if (list.find((f) => f.size > 10 * 1024 * 1024)) return toast.error("Arquivo maior que 10MB não permitido");
    const parsed = await Promise.all(list.map(readAsBase64));
    setReply((r) => ({ ...r, attachments: [...r.attachments, ...parsed] }));
  };

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActiveTicket(data);
      setReply({ body: "", attachments: [] });
      setOpenView(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendReply = async () => {
    if (!reply.body.trim() && reply.attachments.length === 0) return;
    setSavingReply(true);
    try {
      await api.post(`/support/tickets/${activeTicket.id}/messages`, reply);
      setReply({ body: "", attachments: [] });
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingReply(false); }
  };

  const closeTicket = async () => {
    try {
      await api.post(`/support/tickets/${activeTicket.id}/close`);
      toast.success("Chamado concluído");
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reopenTicket = async () => {
    try {
      await api.post(`/support/tickets/${activeTicket.id}/reopen`);
      toast.success("Chamado reaberto");
      const { data } = await api.get(`/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      await api.put("/service-status", status);
      toast.success("Status do serviço atualizado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingStatus(false); }
  };

  const filtered = tickets.filter((t) => t.status === tab);
  const meta = STATUS_META[status.overall_status] || STATUS_META.operante;

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Admin</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Suporte</h1>
        <p className="text-sm text-[#5C5C59] mt-2">Gerencie os chamados e o status geral da plataforma.</p>
      </div>

      {/* Service status card */}
      <div className={`mb-6 p-5 border ${meta.color}`} data-testid="master-status-card">
        <div className="flex items-center gap-2 mb-3">
          <Heartbeat size={18} weight="fill" />
          <div className="font-bold uppercase tracking-widest text-xs">Status dos serviços</div>
          <span className={`ml-auto inline-block w-3 h-3 rounded-full ${meta.dot}`}></span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Situação</Label>
            <Select value={status.overall_status} onValueChange={(v) => setStatus({ ...status, overall_status: v })}>
              <SelectTrigger data-testid="master-status-select" className="rounded-none mt-1 bg-white text-[#1A1A1A]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Mensagem exibida aos clientes</Label>
            <Input value={status.message} onChange={(e) => setStatus({ ...status, message: e.target.value })} data-testid="master-status-msg" className="rounded-none mt-1 bg-white text-[#1A1A1A]" placeholder="Todos os sistemas operando normalmente." />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Número WhatsApp Suporte</Label>
            <Input value={status.whatsapp_number} onChange={(e) => setStatus({ ...status, whatsapp_number: e.target.value })} data-testid="master-status-wa" className="rounded-none mt-1 bg-white text-[#1A1A1A]" placeholder="5511999998888" />
          </div>
          <div className="md:col-span-2 flex items-end justify-end">
            <Button onClick={saveStatus} disabled={savingStatus} data-testid="master-status-save" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
              <FloppyDisk size={14} weight="bold" className="mr-1" />
              {savingStatus ? "Salvando…" : "Salvar status"}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-2">
        <button onClick={() => setTab("open")} data-testid="master-tab-open" className={`px-4 py-2 text-sm font-semibold border ${tab === "open" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white text-[#1A1A1A] border-[#E5E5E0]"}`}>
          Abertos ({tickets.filter((t) => t.status === "open").length})
        </button>
        <button onClick={() => setTab("closed")} data-testid="master-tab-closed" className={`px-4 py-2 text-sm font-semibold border ${tab === "closed" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white text-[#1A1A1A] border-[#E5E5E0]"}`}>
          Concluídos ({tickets.filter((t) => t.status === "closed").length})
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal">
        <div className="grid grid-cols-[auto_1fr_1fr_auto_auto] items-center gap-3 px-4 py-2 bg-[#F7F7F5] border-b border-[#E5E5E0] text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">
          <div>Data</div><div>Assunto</div><div>Empresa</div><div>Autor</div><div>Ação</div>
        </div>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhum chamado nesta aba.</div>
        )}
        {filtered.map((t) => (
          <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`master-tix-row-${t.id}`} className="w-full grid grid-cols-[auto_1fr_1fr_auto_auto] items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] last:border-b-0 hover:bg-[#F7F7F5] transition text-left">
            <div className="text-xs text-[#5C5C59]">
              {new Date(t.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
            </div>
            <div className="font-semibold text-sm truncate">{t.subject}</div>
            <div className="text-xs text-[#5C5C59] truncate">{t.company_name || "—"}</div>
            <div className="text-[10px] text-[#5C5C59]">{t.created_by_name}</div>
            <div className="text-[10px] text-[#0F4C3A] font-semibold">Abrir →</div>
          </button>
        ))}
      </div>

      {/* View ticket dialog — same UX as client */}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent className="bg-white rounded-none max-w-2xl max-h-[92vh] overflow-y-auto">
          {activeTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <button onClick={() => setOpenView(false)} className="text-[#5C5C59] hover:text-[#0F4C3A]"><ArrowLeft size={16} weight="bold" /></button>
                  {activeTicket.subject}
                  <span className={`ml-2 text-[10px] px-2 py-0.5 border ${activeTicket.status === "open" ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" : "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]"}`}>
                    {activeTicket.status === "open" ? "Aberto" : "Concluído"}
                  </span>
                </DialogTitle>
                <p className="text-[10px] text-[#5C5C59] mt-1">
                  <b>{activeTicket.company_name || "Sem empresa"}</b> · aberto por {activeTicket.created_by_name} em {new Date(activeTicket.created_at).toLocaleString("pt-BR")}
                </p>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {(activeTicket.messages || []).map((m) => {
                  const isMaster = m.sender_role === "master";
                  return (
                    <div key={m.id} className={`flex ${isMaster ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] p-3 text-sm ${isMaster ? "bg-[#0F4C3A] text-white" : "bg-white border border-[#E5E5E0]"}`}>
                        <div className={`text-[10px] font-bold mb-1 ${isMaster ? "opacity-80" : "text-[#0F4C3A]"}`}>
                          {m.sender_name} {isMaster && "(Você)"}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        {(m.attachments || []).map((a, i) => (
                          <a key={i} href={`data:${a.mimetype};base64,${a.data}`} download={a.filename}
                             className={`block mt-2 text-[10px] underline break-all ${isMaster ? "text-white/80" : "text-[#0F4C3A]"}`}>
                            📎 {a.filename}
                          </a>
                        ))}
                        <div className={`text-[9px] mt-1 ${isMaster ? "text-white/60" : "text-[#5C5C59]"}`}>
                          {new Date(m.created_at).toLocaleString("pt-BR")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#E5E5E0] pt-3 space-y-2">
                <Textarea value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} data-testid="master-reply-body" placeholder="Sua resposta ao cliente…" className="rounded-none min-h-[80px]" />
                <div className="flex flex-wrap gap-2">
                  {reply.attachments.map((a, i) => (
                    <div key={i} className="text-xs bg-[#F7F7F5] px-2 py-1 flex items-center gap-1">
                      <Paperclip size={12} /> {a.filename}
                      <button onClick={() => setReply({ ...reply, attachments: reply.attachments.filter((_, x) => x !== i) })} className="text-[#DC2626] hover:opacity-70"><X size={12} weight="bold" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <button onClick={() => replyAttachRef.current?.click()} data-testid="master-reply-attach-btn" className="text-xs text-[#0F4C3A] hover:underline">
                    <Paperclip size={12} weight="bold" className="inline mr-1" /> Anexar
                  </button>
                  <input ref={replyAttachRef} type="file" multiple className="hidden" onChange={(e) => handleAttachments(e.target.files)} />
                  <div className="flex items-center gap-2">
                    {activeTicket.status === "open" ? (
                      <Button variant="outline" onClick={closeTicket} data-testid="master-close-btn" className="rounded-none">
                        <CheckCircle size={14} weight="bold" className="mr-1" /> Concluir
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={reopenTicket} data-testid="master-reopen-btn" className="rounded-none">
                        <ArrowClockwise size={14} weight="bold" className="mr-1" /> Reabrir
                      </Button>
                    )}
                    <Button onClick={sendReply} disabled={savingReply} data-testid="master-reply-send-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                      <PaperPlaneRight size={14} weight="bold" className="mr-1" />
                      {savingReply ? "Enviando…" : "Enviar"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
